import HighchartsReact from "highcharts-react-official";
import {useMemo} from "react";

import Highcharts from "@/components/Highcharts";
import {MetricSeries} from "@/lib/types/metricsExplorer.ts";
import {ChartEmptyState} from "@/pages/metricsExplorer/charts/ChartEmptyState.tsx";
import {formatMetricValue} from "@/pages/metricsExplorer/chartSeries.ts";
import {collectValues} from "@/pages/metricsExplorer/chartShapes.ts";

import chartStyles from "./Chart.module.scss";

interface DistributionChartProps {
  series: MetricSeries[];
  seriesNames: Record<string, string>;
}

/**
 * How a metric's values are spread, rather than when they occurred.
 *
 * Binning is Highcharts' own: this is a second view of the same data, not a computed
 * parameter, so unlike trend or anomaly figures it does not need to come from the server.
 */
export const DistributionChart = ({series, seriesNames}: DistributionChartProps) => {
  const queried = useMemo(() => series.filter((item) => item.kind === "PRIMARY"), [series]);

  const options = useMemo(() => {
    const baseSeries = queried.map((item, index) => ({
      id: `base-${index}`,
      name: seriesNames[item.key] ?? item.label,
      type: "scatter",
      data: collectValues(item),
      visible: false,
      showInLegend: false,
    }));
    const histograms = queried.map((item, index) => ({
      name: seriesNames[item.key] ?? item.label,
      type: "histogram",
      baseSeries: `base-${index}`,
      // Overlapping bars hide each other once more than one series is charted.
      opacity: queried.length > 1 ? 0.6 : 1,
      custom: {unit: item.unit},
    }));
    return {
      chart: {zooming: {type: "x"}},
      title: {text: ""},
      credits: {enabled: false},
      xAxis: {
        title: {text: "Value"},
        labels: {
          formatter: ({value}: {value: number}) => formatMetricValue(value, queried[0]?.unit ?? null),
        },
      },
      yAxis: {title: {text: "Occurrences"}},
      plotOptions: {histogram: {binsNumber: "sturges"}},
      tooltip: {outside: true},
      series: [...histograms, ...baseSeries],
    };
  }, [queried, seriesNames]);

  if (queried.length === 0 || queried.every((item) => collectValues(item).length === 0)) {
    return (
      <ChartEmptyState
        title="Nothing to distribute"
        message="This range returned no values to bin. Widen the range or pick another metric."
      />
    );
  }

  return (
    <HighchartsReact highcharts={Highcharts} options={options} containerProps={{className: chartStyles.container}} />
  );
};
